import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { fetchZctaAcsSnapshot } from "../../services/census/fetchZctaAcsSnapshot";
import { acsFiveYearVintageLabel, getAcsDatasetYear } from "../../services/census/acsReleaseConfig";
import { getZctaPrimaryPlace } from "../../services/census/zctaPrimaryPlace";

function fmtCount(n) {
  if (n == null) return "—";
  return n.toLocaleString();
}

function fmtPct(n) {
  if (n == null) return "—";
  return `${n.toFixed(1)}%`;
}

function fmtMoney(n) {
  if (n == null) return "—";
  return `$${n.toLocaleString()}`;
}

/**
 * ZCTA detail + ACS snapshot; primary action adds or removes from the collection list.
 */
const ZctaCollectionDetailModal = ({
  open,
  zip,
  isSelected,
  onClose,
  onAddOrRemove,
}) => {
  const zcta = zip ? String(zip.id) : "";

  const [primaryPlace, setPrimaryPlace] = useState(null);

  const titleSuffix = useMemo(() => {
    if (!zip) return "";
    const city = typeof zip.city === "string" ? zip.city.trim() : "";
    const stMap = typeof zip.state === "string" ? zip.state.trim() : "";
    if (city) return stMap ? ` — ${city}, ${stMap}` : ` — ${city}`;
    if (primaryPlace?.city) {
      const st = primaryPlace.state?.trim() || "";
      return st ? ` — ${primaryPlace.city}, ${st}` : ` — ${primaryPlace.city}`;
    }
    return "";
  }, [zip, primaryPlace]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !zcta) {
      setPrimaryPlace(null);
      return;
    }
    let cancelled = false;
    getZctaPrimaryPlace(zcta).then((p) => {
      if (!cancelled) setPrimaryPlace(p);
    });
    return () => {
      cancelled = true;
    };
  }, [open, zcta]);

  useEffect(() => {
    if (!open || !zcta) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    fetchZctaAcsSnapshot(zcta)
      .then((snap) => {
        if (cancelled) return;
        setData(snap);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "Could not load Census data.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, zcta]);

  const vintage = acsFiveYearVintageLabel(data?.acsYear ?? getAcsDatasetYear());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle component="div" sx={{ fontWeight: 700 }}>
        {zcta || ""}
        {titleSuffix}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Census ZIP Code Tabulation Area (ZCTA). Estimates below are area-level, not individual patients.
          {primaryPlace && !(zip?.city && String(zip.city).trim()) ? (
            <>
              {" "}
              The title locality is the primary USPS place name for this code from{" "}
              <Link href="https://www.geonames.org/" target="_blank" rel="noopener noreferrer">
                GeoNames
              </Link>{" "}
              (CC BY 4.0) where ZCTA matches ZIP; some areas may differ.
            </>
          ) : null}
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Insurance percentages are for the civilian noninstitutionalized population (where applicable).
          Age 65+ is share of total population.
        </Alert>

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
            <CircularProgress size={28} />
            <Typography variant="body2">Loading {vintage}…</Typography>
          </Box>
        ) : null}

        {error ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}

        {!loading && !error && data === null ? (
          <Typography variant="body2" color="text.secondary">
            No published ACS rows returned for this ZCTA (boundary or data suppression).
          </Typography>
        ) : null}

        {!loading && data ? (
          <Stack spacing={1.25}>
            <Typography variant="caption" color="text.secondary">
              Source: {vintage}. Medicaid row is &quot;alone or in combination&quot;; public coverage includes
              Medicare, Medicaid, VA, etc.
            </Typography>
            <Divider />
            <StatRow label="Population (total)" value={fmtCount(data.totalPopulation)} />
            <StatRow label="Male (% of total pop.)" value={fmtPct(data.pctMale)} />
            <StatRow label="Female (% of total pop.)" value={fmtPct(data.pctFemale)} />
            <StatRow label="Median household income" value={fmtMoney(data.medianHouseholdIncome)} />
            <StatRow label="Age 65+ (% of total pop.)" value={fmtPct(data.pctAge65Plus)} />
            <Divider sx={{ my: 0.5 }} />
            <StatRow label="Uninsured (%)" value={fmtPct(data.pctUninsured)} />
            <StatRow label="Insured (approx. 100% − uninsured)" value={fmtPct(data.pctInsuredApprox)} />
            <StatRow label="Private insurance (%)" value={fmtPct(data.pctPrivateInsurance)} />
            <StatRow label="Any public coverage (%)" value={fmtPct(data.pctPublicCoverage)} />
            <StatRow
              label="Medicaid / means-tested public (% of pop.)"
              value={fmtPct(data.pctMedicaidMeansTested)}
            />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          color={isSelected ? "secondary" : "primary"}
          disabled={!zcta}
          onClick={() => {
            if (zcta) onAddOrRemove(zcta);
            onClose();
          }}
        >
          {isSelected ? "Remove from list" : "Add to list"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function StatRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Stack>
  );
}

export default ZctaCollectionDetailModal;
